import { Button } from "@/components/ui/button/button";
import { SettingsContainer } from "@/components/ui/SettingsContainer";
import Typography from "@/components/ui/typography";
import { useAuth } from "@/context/auth-context";
import { Country } from "@/features/countries/schema";
import useGetCountries from "@/features/countries/useGetCountries";
import useGetStates from "@/features/states/useGetStates";
import { format, parse } from "date-fns";
import { ArrowLeftIcon, Mail, MapPin, User, X } from "lucide-react";
import { useRouter } from "@tanstack/react-router";
import PageLoader from "@/components/ui/PageLoader";

const getDialCodeByCountryId = (countryId: number, countries: Country[]) => {
	const country = countries.find((c) => c.id === countryId);
	return country?.calling_code ?? "";
};

const formatPhoneDisplay = (
	phoneNumber: string,
	countryId: number,
	countries: Country[],
) => {
	const dialCode = getDialCodeByCountryId(countryId, countries);
	const formatted = dialCode ? `${dialCode} ${phoneNumber}` : phoneNumber;
	return formatted || "—";
};

const formatDateOfBirth = (dob: string | undefined) => {
	if (!dob) return "—";
	try {
		const parsed = parse(dob, "yyyy-MM-dd", new Date());
		return format(parsed, "MMM d, yyyy");
	} catch {
		return dob;
	}
};

type InfoRowProps = {
	label: string;
	value: string;
};

const InfoRow = ({ label, value }: InfoRowProps) => (
	<div className="py-3 border-b border-gray-100 last:border-0">
		<Typography variant="p" color="primary-light" className="text-sm mb-0.5">
			{label}
		</Typography>
		<Typography variant="p" className="font-medium text-textDark text-base">
			{value || "—"}
		</Typography>
	</div>
);

type PersonalInfoDisplayProps = {
	countries: { countries: Country[] };
};

const PersonalInfoDisplay = ({ countries }: PersonalInfoDisplayProps) => {
	const { user } = useAuth();
	const router = useRouter();

	const countryName =
		countries.countries.find((c) => c.id === user.global_country_id)?.name ??
		"—";

	const { data: statesData } = useGetStates(
		user.global_country_id > 0 ? user.global_country_id : 1,
	);

	const stateName =
		statesData?.states.find((s) => s.id === user.global_state_id)?.name ?? "—";

	return (
		<SettingsContainer className="flex flex-col max-w-screen-sm bg-white text-textDark">
			<div className="mb-6 flex justify-end md:justify-start">
				<Button
					variant="ghost"
					size="compact"
					className="self-start"
					onClick={() => router.history.back()}
				>
					<X className="w-6 h-6 text-textDark md:hidden" />
					<ArrowLeftIcon className="w-6 h-6 text-textDark hidden md:block" />
				</Button>
			</div>

			<div className="text-left w-full mb-6">
				<Typography variant="h5">Personal Info</Typography>
				<Typography variant="p" color="primary-light" className="mt-1 text-sm">
					Your account information
				</Typography>
			</div>

			<div className="space-y-6">
				{/* Personal details */}
				<section className="rounded-xl p-4 md:p-5 border-b border-gray-100 last:border-b-0">
					<div className="flex items-center gap-2 mb-4">
						<div className="w-8 h-8 rounded-lg bg-primary-600 flex items-center justify-center">
							<User className="w-4 h-4 text-white" />
						</div>
						<Typography variant="h6" className="text-textDark text-base">
							Personal Details
						</Typography>
					</div>
					<div className="space-y-0">
						<InfoRow label="First name" value={user.first_name ?? ""} />
						<InfoRow label="Last name" value={user.last_name ?? ""} />
						<InfoRow
							label="Date of birth"
							value={formatDateOfBirth(user.date_of_birth)}
						/>
					</div>
				</section>

				{/* Contact */}
				<section className="rounded-xl p-4 md:p-5 border-b border-gray-100 last:border-b-0">
					<div className="flex items-center gap-2 mb-4">
						<div className="w-8 h-8 rounded-lg bg-primary-600 flex items-center justify-center">
							<Mail className="w-4 h-4 text-white" />
						</div>
						<Typography variant="h6" className="text-textDark text-base">
							Contact
						</Typography>
					</div>
					<div className="space-y-0">
						<InfoRow label="Email" value={user.email ?? ""} />
						<InfoRow
							label="Phone"
							value={formatPhoneDisplay(
								user.phone_number ?? "",
								user.phone_global_country_id,
								countries.countries,
							)}
						/>
					</div>
				</section>

				{/* Address */}
				<section className="rounded-xl p-4 md:p-5 border-b border-gray-100 last:border-b-0">
					<div className="flex items-center gap-2 mb-4">
						<div className="w-8 h-8 rounded-lg bg-primary-600 flex items-center justify-center">
							<MapPin className="w-4 h-4 text-white" />
						</div>
						<Typography variant="h6" className="text-textDark text-base">
							Address
						</Typography>
					</div>
					<div className="space-y-0">
						<InfoRow label="Street address" value={user.address ?? ""} />
						{user.apt_number && (
							<InfoRow label="Apt/Suite" value={user.apt_number} />
						)}
						<InfoRow label="City" value={user.city ?? ""} />
						<InfoRow label="State" value={stateName} />
						<InfoRow label="Country" value={countryName} />
						<InfoRow label="Zip code" value={user.zipcode ?? ""} />
					</div>
				</section>
			</div>
		</SettingsContainer>
	);
};

const PersonalInfoSettingsPage = () => {
	const countryQuery = useGetCountries();

	return (
		<PageLoader isFullscreenLoading={true} query={countryQuery}>
			{(countries) => <PersonalInfoDisplay countries={countries} />}
		</PageLoader>
	);
};

export default PersonalInfoSettingsPage;
